import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, User, Phone, BadgeCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { getDrivers, createDriver, updateDriver, deleteDriver } from '../../lib/api';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';

export default function DriversPage() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    badge_number: '',
    pin: '',
    phone: '',
    is_active: true
  });

  useEffect(() => {
    loadDrivers();
  }, []);

  const loadDrivers = async () => {
    try {
      const response = await getDrivers();
      setDrivers(response.data);
    } catch (error) {
      toast.error('Failed to load drivers');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      badge_number: '',
      pin: '',
      phone: '',
      is_active: true
    });
    setEditingDriver(null);
  };

  const handleEdit = (driver) => {
    setEditingDriver(driver);
    setFormData({
      name: driver.name,
      badge_number: driver.badge_number,
      pin: '',
      phone: driver.phone || '',
      is_active: driver.is_active
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.badge_number.trim()) {
      toast.error('Name and badge number are required');
      return;
    }

    if (!editingDriver && (!formData.pin || formData.pin.length !== 4)) {
      toast.error('Please enter a 4-digit PIN');
      return;
    }

    setSaving(true);
    try {
      if (editingDriver) {
        const updateData = {
          name: formData.name,
          badge_number: formData.badge_number,
          phone: formData.phone || null,
          is_active: formData.is_active
        };
        if (formData.pin) {
          updateData.pin = formData.pin;
        }
        await updateDriver(editingDriver.id, updateData);
        toast.success('Driver updated successfully');
      } else {
        await createDriver({
          name: formData.name,
          badge_number: formData.badge_number,
          pin: formData.pin,
          phone: formData.phone || null
        });
        toast.success('Driver created successfully');
      }
      
      setShowForm(false);
      resetForm();
      loadDrivers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save driver');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    
    try {
      await deleteDriver(deleteTarget.id);
      toast.success('Driver deleted');
      loadDrivers();
    } catch (error) {
      toast.error('Failed to delete driver');
    } finally {
      setDeleteTarget(null);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="drivers-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-blue-900">Drivers</h1>
            <p className="text-slate-500">Manage driver accounts</p>
          </div>
          <Button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="bg-yellow-500 hover:bg-yellow-600 text-blue-950 font-bold btn-press"
            data-testid="add-driver-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Driver
          </Button>
        </div>

        {/* Drivers List */}
        {drivers.length === 0 ? (
          <div className="empty-state" data-testid="empty-drivers">
            <User className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-medium text-slate-600">No drivers added yet</p>
            <p className="text-slate-400 mb-4">Add your first driver to get started</p>
            <Button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="bg-yellow-500 hover:bg-yellow-600 text-blue-950"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add First Driver
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="drivers-list">
            {drivers.map((driver, idx) => (
              <Card key={driver.id} className="card-hover border-slate-200" data-testid={`driver-card-${idx}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                        <User className="w-6 h-6 text-blue-900" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{driver.name}</h3>
                        <div className="flex items-center gap-1 text-sm text-slate-500">
                          <BadgeCheck className="w-4 h-4" />
                          #{driver.badge_number}
                        </div>
                      </div>
                    </div>
                    <Badge className={driver.is_active ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'}>
                      {driver.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>

                  {driver.phone && (
                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                      <Phone className="w-4 h-4" />
                      {driver.phone}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(driver)}
                      className="flex-1"
                      data-testid={`edit-driver-${idx}`}
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteTarget(driver)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-driver-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingDriver ? 'Edit Driver' : 'Add New Driver'}</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="form-group">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Smith"
                  data-testid="driver-name-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="badge">Badge Number *</Label>
                <Input
                  id="badge"
                  value={formData.badge_number}
                  onChange={(e) => setFormData({ ...formData, badge_number: e.target.value })}
                  placeholder="D001"
                  data-testid="driver-badge-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="pin">
                  4-Digit PIN {editingDriver ? '(leave blank to keep current)' : '*'}
                </Label>
                <Input
                  id="pin"
                  type="password"
                  maxLength={4}
                  value={formData.pin}
                  onChange={(e) => setFormData({ ...formData, pin: e.target.value.replace(/\D/g, '') })}
                  placeholder="****"
                  data-testid="driver-pin-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="phone">Phone (optional)</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="07123 456789"
                  data-testid="driver-phone-input"
                />
              </div>

              {editingDriver && (
                <div className="flex items-center justify-between">
                  <Label htmlFor="active">Active Status</Label>
                  <Switch
                    id="active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    data-testid="driver-active-switch"
                  />
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-yellow-500 hover:bg-yellow-600 text-blue-950 font-bold"
                  data-testid="save-driver-btn"
                >
                  {saving ? 'Saving...' : (editingDriver ? 'Update' : 'Add Driver')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Driver?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-btn"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
